// Layer 1. What a reviewer reads before deciding whether to read anything else.
//
// The claim, one line of context, where it is in the document, and how it was
// established. No failure-mode code, no tier, no RRP row: those are provenance,
// and provenance lives in layer 3. See docs/redesign/IA.md.
//
// The decision is the rest of layer 1 and the card renders it directly after
// this block, not inside it — see FindingCard.tsx for why.

import { EvidenceBadge } from '../primitives';
import { Glossed } from '../Glossary';
import { evidenceBadgeFor } from '../../data/report';
import { locationChip } from '../../data/review';
import type { Flag } from '../../types';

// The location chip is the comment sheet's location column, so there is one
// implementation of it and it lives with the review. Re-exported here because
// this is where callers have always reached for it.
export { locationChip };

interface FindingClaimProps {
  finding: Flag;
  /** The id the card's aria-labelledby points at. */
  titleId: string;
  /**
   * Takes the reviewer to this finding's page in the document pane, with the
   * quote marked and focused. Where it is given, the location chip becomes the
   * control that does it — a citation you can follow is the ordinary pattern,
   * and it means the pane has a keyboard entry point without a new affordance
   * being invented for it. Where it is not, the chip is the text it always was.
   */
  onShowInDocument?(): void;
}

export default function FindingClaim({
  finding,
  titleId,
  onShowInDocument,
}: FindingClaimProps) {
  const badge = evidenceBadgeFor(finding);
  const chip = locationChip(finding.sectionRef, finding.page);
  const followable = chip !== null && onShowInDocument !== undefined;

  return (
    <div className="fc__claim">
      {/* Section titles wrap. Nothing here truncates. Focusable so that "Back
          to the finding" in the document pane has somewhere to land. */}
      <h2 className="fc__claim-title" id={titleId} tabIndex={-1}>
        <Glossed>{finding.claim}</Glossed>
      </h2>

      {finding.contextLine ? (
        <p className="fc__claim-context">{finding.contextLine}</p>
      ) : null}

      <div className="fc__claim-meta">
        {followable ? (
          <button
            type="button"
            className="fc__chip fc__chip--follow"
            onClick={onShowInDocument}
            aria-label={`Show ${chip} in the document`}
          >
            {chip}
          </button>
        ) : chip ? (
          <span className="fc__chip">{chip}</span>
        ) : null}
        <EvidenceBadge id={badge.id} />
      </div>
    </div>
  );
}
